import { useEffect, useState } from "react";
import {
  LogOut,
  RotateCcw,
  Save,
  ExternalLink,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Loader2,
  Lock,
  ImageIcon,
  Video,
} from "lucide-react";
import clsx from "clsx";
import { SECTIONS, type FieldDef } from "./schema";
import MediaLibrary from "./MediaLibrary";
import { login, fetchCMSData, saveCMSData, resetCMSData } from "./api/cms";

const TOKEN_KEY = "doudou_admin_token";

type AnyRecord = Record<string, any>;
type Notice = { type: "ok" | "err"; text: string } | null;

export default function AdminApp() {
  const [token, setToken] = useState<string | null>(() => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  });
  const [content, setContent] = useState<AnyRecord | null>(null);
  const [activeKey, setActiveKey] = useState(SECTIONS[0].key);
  const [notice, setNotice] = useState<Notice>(null);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const notify = (type: "ok" | "err", text: string) => {
    setNotice({ type, text });
    window.setTimeout(() => setNotice(null), 3500);
  };

  const logout = () => {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      // ignore
    }
    setToken(null);
    setContent(null);
  };

  useEffect(() => {
    if (!token) return;
    const load = async () => {
      try {
        const data = await fetchCMSData(token);
        setContent(data);
      } catch {
        notify("err", "無法載入內容，請重新整理");
      }
    };
    load();
  }, [token]);

  const saveSection = async () => {
    if (!content || !token) return;
    setSaving(true);
    try {
      const data = await saveCMSData(activeKey, content[activeKey], token);
      setContent(data);
      notify("ok", "已儲存，網站即時生效");
    } catch (e) {
      notify("err", e instanceof Error ? e.message : "儲存失敗");
    } finally {
      setSaving(false);
    }
  };

  const resetAll = async () => {
    if (!token) return;
    if (!window.confirm("確定還原全部內容為預設值？此操作會覆蓋所有修改。")) return;
    setResetting(true);
    try {
      const data = await resetCMSData(token);
      setContent(data);
      notify("ok", "已還原預設內容");
    } catch (e) {
      notify("err", e instanceof Error ? e.message : "還原失敗");
    } finally {
      setResetting(false);
    }
  };

  if (!token) {
    return <LoginCard onLogin={(t) => setToken(t)} />;
  }

  const section = SECTIONS.find((s) => s.key === activeKey) ?? SECTIONS[0];

  return (
    <div className="min-h-screen bg-deep-navy text-white">
      <header className="sticky top-0 z-30 border-b border-neon-cyan/15 bg-deep-black/80 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-neon-cyan to-neon-purple font-bold text-deep-navy">
              D
            </span>
            <div>
              <p className="text-base font-bold">Doudou 內容管理後台</p>
              <p className="text-xs text-gray-500">修改儲存後網站即時生效，無需重新部署</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium text-neon-cyan transition hover:bg-neon-cyan/10"
            >
              <ExternalLink className="h-4 w-4" />
              查看網站
            </a>
            <button
              type="button"
              onClick={resetAll}
              disabled={resetting || !content}
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium text-gray-400 transition hover:bg-white/5 disabled:opacity-50"
            >
              {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
              還原預設
            </button>
            <button
              type="button"
              onClick={logout}
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium text-gray-400 transition hover:bg-white/5"
            >
              <LogOut className="h-4 w-4" />
              登出
            </button>
          </div>
        </div>
      </header>

      {notice && (
        <div
          className={clsx(
            "fixed left-1/2 top-20 z-50 -translate-x-1/2 rounded-full px-6 py-2.5 text-sm font-medium text-white shadow-lg",
            notice.type === "ok" ? "bg-neon-cyan text-deep-navy" : "bg-neon-pink"
          )}
        >
          {notice.text}
        </div>
      )}

      <div className="mx-auto grid max-w-7xl gap-6 px-4 py-8 lg:grid-cols-[240px_1fr]">
        <nav className="flex gap-2 overflow-x-auto lg:flex-col">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setActiveKey(s.key)}
              className={clsx(
                "whitespace-nowrap rounded-xl px-4 py-2.5 text-left text-sm font-medium transition",
                s.key === activeKey
                  ? "bg-gradient-to-r from-neon-cyan to-neon-purple text-deep-navy shadow"
                  : "neon-card text-gray-300 hover:border-neon-cyan/50"
              )}
            >
              {s.label}
            </button>
          ))}
        </nav>

        <main className="rounded-2xl neon-card p-6">
          {!content ? (
            <div className="flex items-center justify-center py-20 text-gray-500">
              <Loader2 className="mr-2 h-5 w-5 animate-spin text-neon-cyan" />
              載入中…
            </div>
          ) : (
            <>
              <div className="mb-6 flex items-center justify-between border-b border-neon-cyan/15 pb-4">
                <h1 className="text-xl font-bold text-white">{section.label}</h1>
                <button
                  type="button"
                  onClick={saveSection}
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-neon-cyan to-neon-purple px-5 py-2.5 text-sm font-bold text-deep-navy transition hover:brightness-110 disabled:opacity-50"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  儲存此區塊
                </button>
              </div>
              <div className="space-y-5">
                {section.fields.map((def) => (
                  <FieldRenderer
                    key={def.key}
                    def={def}
                    value={content[activeKey]?.[def.key]}
                    onChange={(v) =>
                      setContent({
                        ...content,
                        [activeKey]: { ...content[activeKey], [def.key]: v },
                      })
                    }
                  />
                ))}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

function LoginCard({ onLogin }: { onLogin: (token: string) => void }) {
  const [username, setUsername] = useState("doudou");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const data = await login({ username, password });
      try {
        localStorage.setItem(TOKEN_KEY, data.token);
      } catch {
        // ignore
      }
      onLogin(data.token);
    } catch (err) {
      setError(err instanceof Error ? err.message : "登入失敗");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-deep-navy px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-2xl neon-card p-8 shadow-lg"
      >
        <div className="mb-6 flex flex-col items-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-neon-cyan to-neon-purple text-deep-navy">
            <Lock className="h-5 w-5" />
          </span>
          <h1 className="mt-4 text-xl font-bold text-white">Doudou CMS 後台</h1>
          <p className="mt-1 text-sm text-gray-500">請輸入管理員密碼</p>
        </div>

        <label className="block text-sm font-medium text-gray-300">帳號</label>
        <input
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoComplete="username"
          required
          className="mt-1 w-full rounded-lg border border-neon-cyan/30 bg-deep-black px-3 py-2.5 text-sm text-white outline-none transition focus:border-neon-cyan"
        />

        <label className="mt-4 block text-sm font-medium text-gray-300">密碼</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
          className="mt-1 w-full rounded-lg border border-neon-cyan/30 bg-deep-black px-3 py-2.5 text-sm text-white outline-none transition focus:border-neon-cyan"
        />

        {error && <p className="mt-3 text-sm text-neon-pink">{error}</p>}

        <button
          type="submit"
          disabled={loading}
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-neon-cyan to-neon-purple py-3 text-sm font-bold text-deep-navy transition hover:brightness-110 disabled:opacity-50"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          登入
        </button>
      </form>
    </div>
  );
}

function FieldRenderer({
  def,
  value,
  onChange,
}: {
  def: FieldDef;
  value: any;
  onChange: (v: any) => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);

  if (def.kind === "object") {
    const obj = value ?? {};
    return (
      <fieldset className="rounded-xl border border-neon-cyan/20 p-4">
        <legend className="px-2 text-sm font-semibold text-neon-cyan">{def.label}</legend>
        <div className="space-y-4">
          {def.fields.map((f) => (
            <FieldRenderer
              key={f.key}
              def={f}
              value={obj[f.key]}
              onChange={(v) => onChange({ ...obj, [f.key]: v })}
            />
          ))}
        </div>
      </fieldset>
    );
  }

  if (def.kind === "list") {
    return <ListEditor def={def} value={Array.isArray(value) ? value : []} onChange={onChange} />;
  }

  if (def.kind === "stringList") {
    return <StringListEditor def={def} value={Array.isArray(value) ? value : []} onChange={onChange} />;
  }

  return (
    <div>
      <label className="block text-sm font-medium text-gray-300">{def.label}</label>
      {def.kind === "text" ? (
        <textarea
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          className="mt-1 w-full rounded-lg border border-neon-cyan/30 bg-deep-black px-3 py-2.5 text-sm text-white outline-none transition focus:border-neon-cyan"
        />
      ) : def.kind === "boolean" ? (
        <select
          value={value ? "true" : "false"}
          onChange={(e) => onChange(e.target.value === "true")}
          className="mt-1 w-full rounded-lg border border-neon-cyan/30 bg-deep-black px-3 py-2.5 text-sm text-white outline-none transition focus:border-neon-cyan"
        >
          <option value="true">是</option>
          <option value="false">否</option>
        </select>
      ) : (
        <input
          type="text"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className="mt-1 w-full rounded-lg border border-neon-cyan/30 bg-deep-black px-3 py-2.5 text-sm text-white outline-none transition focus:border-neon-cyan"
        />
      )}
      {(def.kind === "image" || def.kind === "video") && (
        <div className="mt-2 flex items-center gap-3">
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-full bg-neon-cyan/10 px-3 py-1.5 text-xs font-medium text-neon-cyan transition hover:bg-neon-cyan/20"
          >
            {def.kind === "video" ? (
              <Video className="h-3.5 w-3.5" />
            ) : (
              <ImageIcon className="h-3.5 w-3.5" />
            )}
            {def.kind === "video" ? "從媒體庫選擇影片" : "從媒體庫選擇"}
          </button>
          {typeof value === "string" && value && (
            <>
              {def.kind === "video" ? (
                <video
                  src={value}
                  muted
                  className="h-16 rounded-lg border border-neon-cyan/20 object-cover"
                />
              ) : (
                <img src={value} alt="預覽" className="h-16 rounded-lg border border-neon-cyan/20 object-cover" />
              )}
            </>
          )}
          <MediaLibrary
            isOpen={pickerOpen}
            onClose={() => setPickerOpen(false)}
            onSelect={(url) => onChange(url)}
          />
        </div>
      )}
      {def.hint && <p className="mt-1 text-xs text-gray-500">{def.hint}</p>}
    </div>
  );
}

function StringListEditor({
  def,
  value,
  onChange,
}: {
  def: Extract<FieldDef, { kind: "stringList" }>;
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const addItem = () => onChange([...value, ""]);
  const removeItem = (i: number) => onChange(value.filter((_, idx) => idx !== i));
  const updateItem = (i: number, text: string) => {
    const next = [...value];
    next[i] = text;
    onChange(next);
  };

  return (
    <div className="rounded-xl border border-neon-cyan/20 p-4">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm font-semibold text-neon-cyan">
          {def.label}
          <span className="ml-2 text-xs font-normal text-gray-500">共 {value.length} 項</span>
        </p>
        <button
          type="button"
          onClick={addItem}
          className="inline-flex items-center gap-1 rounded-full bg-neon-cyan/10 px-3 py-1.5 text-xs font-medium text-neon-cyan transition hover:bg-neon-cyan/20"
        >
          <Plus className="h-3.5 w-3.5" />
          新增{def.itemName}
        </button>
      </div>
      <div className="space-y-2">
        {value.map((item, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              type="text"
              value={item}
              onChange={(e) => updateItem(i, e.target.value)}
              className="flex-1 rounded-lg border border-neon-cyan/30 bg-deep-black px-3 py-2 text-sm text-white outline-none transition focus:border-neon-cyan"
            />
            <button
              type="button"
              onClick={() => removeItem(i)}
              className="rounded-lg p-2 text-gray-500 transition hover:bg-neon-pink/10 hover:text-neon-pink"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        {value.length === 0 && <p className="py-2 text-center text-sm text-gray-500">暫無項目</p>}
      </div>
    </div>
  );
}

function ListEditor({
  def,
  value,
  onChange,
}: {
  def: Extract<FieldDef, { kind: "list" }>;
  value: any[];
  onChange: (v: any[]) => void;
}) {
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  const addItem = () => {
    const template: AnyRecord = {};
    def.fields.forEach((f) => {
      if (f.kind === "list") template[f.key] = [];
      else if (f.kind === "stringList") template[f.key] = [];
      else if (f.kind === "object") template[f.key] = {};
      else if (f.kind === "boolean") template[f.key] = false;
      else template[f.key] = "";
    });
    onChange([...value, template]);
    setOpenIdx(value.length);
  };

  const removeItem = (i: number) => {
    if (!window.confirm(`確定刪除這個${def.itemName}？`)) return;
    onChange(value.filter((_, idx) => idx !== i));
    setOpenIdx(null);
  };

  const itemTitle = (item: AnyRecord, i: number) => {
    const labelKey = def.fields.find((f) =>
      ["title", "label", "name", "q", "day"].includes(f.key)
    );
    const label = labelKey ? item[labelKey.key] : "";
    return label || `${def.itemName} ${i + 1}`;
  };

  return (
    <div className="rounded-xl border border-neon-cyan/20 p-4">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm font-semibold text-neon-cyan">
          {def.label}
          <span className="ml-2 text-xs font-normal text-gray-500">共 {value.length} 項</span>
        </p>
        <button
          type="button"
          onClick={addItem}
          className="inline-flex items-center gap-1 rounded-full bg-neon-cyan/10 px-3 py-1.5 text-xs font-medium text-neon-cyan transition hover:bg-neon-cyan/20"
        >
          <Plus className="h-3.5 w-3.5" />
          新增{def.itemName}
        </button>
      </div>

      <div className="space-y-2">
        {value.map((item, i) => {
          const isOpen = openIdx === i;
          return (
            <div key={i} className="rounded-lg border border-neon-cyan/15 bg-deep-black/50">
              <div className="flex items-center justify-between gap-2 px-3 py-2.5">
                <button
                  type="button"
                  onClick={() => setOpenIdx(isOpen ? null : i)}
                  className="flex flex-1 items-center gap-2 text-left text-sm font-medium text-gray-300"
                >
                  {isOpen ? (
                    <ChevronUp className="h-4 w-4 text-neon-cyan" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  )}
                  <span className="truncate">{itemTitle(item, i)}</span>
                </button>
                <button
                  type="button"
                  onClick={() => removeItem(i)}
                  aria-label="刪除"
                  className="rounded-full p-1.5 text-gray-500 transition hover:bg-neon-pink/10 hover:text-neon-pink"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              {isOpen && (
                <div className="space-y-4 border-t border-neon-cyan/15 bg-deep-black p-4">
                  {def.fields.map((f) => (
                    <FieldRenderer
                      key={f.key}
                      def={f}
                      value={item[f.key]}
                      onChange={(v) => {
                        const next = [...value];
                        next[i] = { ...item, [f.key]: v };
                        onChange(next);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {value.length === 0 && (
          <p className="py-4 text-center text-sm text-gray-500">暫無項目，點上方「新增」</p>
        )}
      </div>
    </div>
  );
}
